import React from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Slider, Typography, Tooltip } from '@mui/material';
import {
    ArrowUpward,
    ArrowDownward,
    ArrowBack,
    ArrowForward,
    ZoomIn,
    ZoomOut,
    RestartAlt
} from '@mui/icons-material';
import { PALETTE } from '../theme';

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.1;
const PAN_STEP = 0.1;
const EPS = 0.001;

const clampPan = (v) => Math.max(-1, Math.min(1, Math.round(v * 100) / 100));
const clampZoom = (v) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.round(v * 100) / 100));

// Render the percent shift for the X/Y readouts. Positive X = right, positive
// Y = down (toward the bottom of the source). Show 0 with no sign for clarity.
const fmtShift = (v) => {
    const pct = Math.round(v * 100);
    if (pct === 0) return '0%';
    return (pct > 0 ? '+' : '') + pct + '%';
};

const PhotoFramingControl = ({ framing, onChange, disabled, slack }) => {
    const setFraming = (next) => {
        onChange({
            offsetX: clampPan(next.offsetX ?? framing.offsetX),
            offsetY: clampPan(next.offsetY ?? framing.offsetY),
            zoom: clampZoom(next.zoom ?? framing.zoom),
        });
    };

    const move = (dx, dy) => setFraming({
        offsetX: framing.offsetX + dx,
        offsetY: framing.offsetY + dy,
    });

    const zoomBy = (dz) => setFraming({ zoom: framing.zoom + dz });

    const reset = () => onChange({ offsetX: 0, offsetY: 0, zoom: 1 });

    const slackX = slack?.slackX ?? 1;
    const slackY = slack?.slackY ?? 1;
    const atDefault = framing.offsetX === 0 && framing.offsetY === 0 && framing.zoom === 1;

    // A direction is enabled only if the axis has any pan slack AND the offset
    // hasn't already hit its limit on that side.
    const canMoveLeft  = !disabled && slackX > 0 && framing.offsetX > -1 + EPS;
    const canMoveRight = !disabled && slackX > 0 && framing.offsetX <  1 - EPS;
    const canMoveUp    = !disabled && slackY > 0 && framing.offsetY > -1 + EPS;
    const canMoveDown  = !disabled && slackY > 0 && framing.offsetY <  1 - EPS;
    const canZoomIn    = !disabled && framing.zoom < MAX_ZOOM - EPS;
    const canZoomOut   = !disabled && framing.zoom > MIN_ZOOM + EPS;
    const canReset     = !disabled && !atDefault;

    const arrowBtnSx = {
        width: 40,
        height: 40,
        backgroundColor: '#f1f5f9',
        color: PALETTE.text,
        '&:hover': { backgroundColor: '#e0ecff' },
        '&.Mui-disabled': { backgroundColor: '#f8fafc', color: '#cbd5e1' }
    };

    const arrowBtn = (label, enabled, onClick, Icon) => (
        <Tooltip title={enabled ? label : `${label} (no room)`}>
            <span>
                <IconButton
                    size="small"
                    sx={arrowBtnSx}
                    disabled={!enabled}
                    onClick={onClick}
                    aria-label={label}
                >
                    <Icon fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
    );

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            // On wide screens, vertically center the (short) zoom block against
            // the (tall) D-pad so they look balanced; on mobile they stack and
            // stretching reads better.
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: { xs: 'flex-start', sm: 'flex-start' },
            gap: { xs: 2, sm: 3 },
            flexWrap: 'wrap'
        }}>
            {/* D-pad style arrows + position readout */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: PALETTE.textMuted, fontWeight: 600 }}>
                    Position — X: {fmtShift(framing.offsetX)}, Y: {fmtShift(framing.offsetY)}
                </Typography>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 40px)',
                    gridTemplateRows: 'repeat(3, 40px)',
                    gap: 0.5,
                    alignItems: 'center',
                    justifyItems: 'center'
                }}>
                    <Box />
                    {arrowBtn('Move photo up', canMoveUp, () => move(0, -PAN_STEP), ArrowUpward)}
                    <Box />
                    {arrowBtn('Move photo left', canMoveLeft, () => move(-PAN_STEP, 0), ArrowBack)}
                    {arrowBtn('Reset framing', canReset, reset, RestartAlt)}
                    {arrowBtn('Move photo right', canMoveRight, () => move(PAN_STEP, 0), ArrowForward)}
                    <Box />
                    {arrowBtn('Move photo down', canMoveDown, () => move(0, PAN_STEP), ArrowDownward)}
                    <Box />
                </Box>
            </Box>

            {/* Zoom controls */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: { xs: '100%', sm: 220 }, flexGrow: 1 }}>
                <Typography variant="caption" sx={{ color: PALETTE.textMuted, fontWeight: 600 }}>
                    Zoom — {Math.round(framing.zoom * 100)}%
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {arrowBtn('Zoom out', canZoomOut, () => zoomBy(-ZOOM_STEP), ZoomOut)}
                    <Slider
                        value={framing.zoom}
                        min={MIN_ZOOM}
                        max={MAX_ZOOM}
                        step={ZOOM_STEP}
                        disabled={disabled}
                        onChange={(_, value) => setFraming({ zoom: value })}
                        aria-label="Zoom"
                        sx={{
                            color: PALETTE.primary,
                            '& .MuiSlider-thumb': { width: 18, height: 18 }
                        }}
                    />
                    {arrowBtn('Zoom in', canZoomIn, () => zoomBy(ZOOM_STEP), ZoomIn)}
                </Box>
            </Box>
        </Box>
    );
};

PhotoFramingControl.propTypes = {
    framing: PropTypes.shape({
        offsetX: PropTypes.number.isRequired,
        offsetY: PropTypes.number.isRequired,
        zoom: PropTypes.number.isRequired
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    slack: PropTypes.shape({
        slackX: PropTypes.number,
        slackY: PropTypes.number
    })
};

export default PhotoFramingControl;
